import type { CSSProperties, RefObject } from "react";
import Button from "../../../../../shared/components/Button";
import InfoTooltip from "../../../../../shared/components/InfoTooltip";
import Spinner from "../../../../../shared/components/Spinner";
import { siteTexts } from "../../../../../shared/i18n/siteTexts";
import {
  DEFAULT_TNP_VISUAL_IDENTITY_COLORS,
  isValidHexColor,
  normalizeHexColor,
} from "../../../../../shared/utils/organizerVisualIdentity";
import type {
  OrganizerVisualIdentityBackgroundMode,
  OrganizerVisualIdentityColors,
} from "../../../../auth/shared/types/auth";
import ProfileAccordionSection from "../ProfileAccordionSection";
import UserProfileSectionHeader from "../UserProfileSectionHeader";

type VisualIdentityMode = "tnp_standard" | "custom";

type ProfileVisualIdentityTabProps = {
  open: boolean;
  onToggle: () => void;
  logoInputRef: RefObject<HTMLInputElement | null>;
  publicNameDraft: string;
  logoDraft: string;
  mode: VisualIdentityMode;
  modeDescriptions: Record<VisualIdentityMode, string>;
  colorsDraft: OrganizerVisualIdentityColors;
  backgroundMode: OrganizerVisualIdentityBackgroundMode;
  previewStyle: CSSProperties;
  hasLowContrast: boolean;
  loading: boolean;
  saving: boolean;
  error: string;
  onLogoFile: (file?: File) => void;
  onPublicNameChange: (value: string) => void;
  onRemoveLogo: () => void;
  onModeChange: (mode: VisualIdentityMode) => void;
  onColorChange: (
    field: keyof OrganizerVisualIdentityColors,
    value: string,
  ) => void;
  onBackgroundModeChange: (mode: OrganizerVisualIdentityBackgroundMode) => void;
  onSave: () => void;
};

const VISUAL_IDENTITY_COLOR_FIELDS: Array<keyof OrganizerVisualIdentityColors> =
  ["primaryColor", "secondaryColor", "accentColor"];

export default function ProfileVisualIdentityTab({
  open,
  onToggle,
  logoInputRef,
  publicNameDraft,
  logoDraft,
  mode,
  modeDescriptions,
  colorsDraft,
  backgroundMode,
  previewStyle,
  hasLowContrast,
  loading,
  saving,
  error,
  onLogoFile,
  onPublicNameChange,
  onRemoveLogo,
  onModeChange,
  onColorChange,
  onBackgroundModeChange,
  onSave,
}: ProfileVisualIdentityTabProps) {
  return (
    <ProfileAccordionSection
      id="profile-visual-identity"
      icon="palette"
      title={siteTexts.profile.user.visualIdentity.title}
      subtitle={siteTexts.profile.user.visualIdentity.description}
      open={open}
      onToggle={onToggle}
    >
      <section className="user-profile-visual-identity">
        <UserProfileSectionHeader
          icon="palette"
          title={
            <span className="info-label">
              {siteTexts.profile.user.visualIdentity.title}
              <InfoTooltip content={siteTexts.profile.user.visualIdentity.info} />
            </span>
          }
          description={siteTexts.profile.user.visualIdentity.description}
        />
        <div className="user-profile-visual-identity-notices">
          <div className="user-profile-visual-identity-alert">
            <span className="material-symbols-outlined" aria-hidden="true">
              info
            </span>
            <div>
              <strong>Informação</strong>
              <p>{siteTexts.profile.user.visualIdentity.backgroundNotice}</p>
              <p>
                As cores personalizadas serão aplicadas apenas em materiais e
                telas dos eventos deste organizador.
              </p>
            </div>
          </div>

          {hasLowContrast && (
            <div
              className="user-profile-visual-identity-alert user-profile-visual-identity-alert--warning"
              role="status"
              aria-live="polite"
            >
              <span className="material-symbols-outlined" aria-hidden="true">
                warning
              </span>
              <div>
                <strong>Atenção ao contraste</strong>
                <p>{siteTexts.profile.user.visualIdentity.contrastWarning}</p>
              </div>
            </div>
          )}
        </div>

        <label className="field">
          <span>{siteTexts.profile.user.visualIdentity.publicName}</span>
          <input
            type="text"
            value={publicNameDraft}
            maxLength={100}
            placeholder={siteTexts.profile.user.visualIdentity.publicNamePlaceholder}
            disabled={saving || loading}
            onChange={(event) => onPublicNameChange(event.target.value)}
          />
        </label>
        <div className="user-profile-logo-upload">
          <div className="user-profile-logo-copy">
            <h4 className="info-label">
              {siteTexts.profile.user.visualIdentity.logoTitle}
              <InfoTooltip
                content={siteTexts.profile.user.visualIdentity.logoHelp}
              />
            </h4>
            <p>{siteTexts.profile.user.visualIdentity.logoDescription}</p>
          </div>
          <div className="user-profile-logo-controls">
            <div className="user-profile-logo-preview">
              {logoDraft ? (
                <img
                  src={logoDraft}
                  alt={siteTexts.profile.user.visualIdentity.logoPreviewAlt}
                />
              ) : (
                <span className="material-symbols-outlined" aria-hidden="true">
                  image
                </span>
              )}
            </div>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,.png,.jpg,.jpeg,.webp"
              className="user-profile-logo-input"
              onChange={(event) => onLogoFile(event.target.files?.[0])}
              aria-label={siteTexts.profile.user.visualIdentity.uploadLogo}
            />
            <div className="user-profile-logo-actions">
              <Button
                type="button"
                variant="ghost"
                onClick={() => logoInputRef.current?.click()}
                disabled={saving || loading}
              >
                {logoDraft
                  ? siteTexts.profile.user.visualIdentity.replaceLogo
                  : siteTexts.profile.user.visualIdentity.uploadLogo}
              </Button>
              {logoDraft && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onRemoveLogo}
                  disabled={saving || loading}
                >
                  {siteTexts.profile.user.visualIdentity.removeLogo}
                </Button>
              )}
            </div>
          </div>
        </div>
        <fieldset className="user-profile-organizer-ranking-modes">
          <legend>{siteTexts.profile.user.visualIdentity.mode}</legend>
          {(["tnp_standard", "custom"] as const).map((nextMode) => {
            const isSelected = mode === nextMode;
            const isDisabled = saving || loading;
            return (
              <label
                key={nextMode}
                className={`user-profile-visual-mode-card${
                  isSelected ? " is-selected" : ""
                }${isDisabled ? " is-disabled" : ""}`}
              >
                <input
                  type="radio"
                  name="organizer-visual-identity-mode"
                  checked={isSelected}
                  onChange={() => onModeChange(nextMode)}
                  disabled={isDisabled}
                />
                <span className="user-profile-visual-mode-copy">
                  <strong>
                    {siteTexts.profile.user.visualIdentity.modes[nextMode]}
                  </strong>
                  <small>{modeDescriptions[nextMode]}</small>
                </span>
              </label>
            );
          })}
        </fieldset>

        {loading && (
          <Spinner label={siteTexts.profile.user.visualIdentity.loading} />
        )}

        <fieldset className="user-profile-visual-background-modes">
          <legend>{siteTexts.profile.user.visualIdentity.backgroundTitle}</legend>
          {(["blue", "black", "white"] as const).map((background) => (
            <label
              key={background}
              className={`user-profile-visual-background-option is-${background}${
                backgroundMode === background ? " is-selected" : ""
              }`}
            >
              <input
                type="radio"
                name="organizer-visual-background"
                value={background}
                checked={backgroundMode === background}
                disabled={saving || loading}
                onChange={() => onBackgroundModeChange(background)}
              />
              <span aria-hidden="true" />
              <strong>
                {siteTexts.profile.user.visualIdentity.backgrounds[background]}
              </strong>
            </label>
          ))}
        </fieldset>

        <div className="user-profile-visual-identity-layout">
          <div className="user-profile-visual-identity-fields">
            {VISUAL_IDENTITY_COLOR_FIELDS.map((field) => {
              const readOnly = mode === "tnp_standard";
              const colorValue =
                colorsDraft[field] || DEFAULT_TNP_VISUAL_IDENTITY_COLORS[field];
              return (
                <label className="field user-profile-color-field" key={field}>
                  <span className="info-label">
                    {siteTexts.profile.user.visualIdentity.fields[field]}
                    <InfoTooltip
                      content={
                        siteTexts.profile.user.visualIdentity.usage[field]
                      }
                    />
                  </span>
                  <div className="user-profile-color-inputs">
                    <input
                      type="color"
                      value={
                        isValidHexColor(colorValue)
                          ? colorValue
                          : DEFAULT_TNP_VISUAL_IDENTITY_COLORS[field]
                      }
                      disabled={readOnly || saving || loading}
                      onChange={(event) =>
                        onColorChange(field, event.target.value)
                      }
                      aria-label={
                        siteTexts.profile.user.visualIdentity.fields[field]
                      }
                    />
                    <input
                      type="text"
                      value={colorValue}
                      placeholder={
                        siteTexts.profile.user.visualIdentity.hexPlaceholder
                      }
                      maxLength={7}
                      readOnly={readOnly}
                      disabled={readOnly || saving || loading}
                      onChange={(event) =>
                        onColorChange(field, event.target.value)
                      }
                      onBlur={(event) =>
                        onColorChange(field, normalizeHexColor(event.target.value))
                      }
                    />
                    <span
                      className="user-profile-color-preview"
                      style={{
                        backgroundColor: isValidHexColor(colorValue)
                          ? colorValue
                          : DEFAULT_TNP_VISUAL_IDENTITY_COLORS[field],
                      }}
                      aria-hidden="true"
                    />
                  </div>
                </label>
              );
            })}
          </div>

          <div className="user-profile-visual-preview" style={previewStyle}>
            <h4>{siteTexts.profile.user.visualIdentity.previewTitle}</h4>
            <div className="user-profile-visual-preview-card">
              <strong>{publicNameDraft}</strong>
              <strong>
                {siteTexts.profile.user.visualIdentity.previewRanking}
              </strong>
              {siteTexts.profile.user.visualIdentity.previewPlayers.map(
                (player, index) => (
                  <div
                    className="user-profile-visual-preview-row"
                    key={`${player}-${index}`}
                  >
                    <span>{["🥇", "🥈", "🥉"][index]}</span>
                    <b>{player}</b>
                    <small>{[120, 110, 95][index]} pts</small>
                  </div>
                ),
              )}
              <button type="button">
                {siteTexts.profile.user.visualIdentity.previewShare}
              </button>
            </div>
          </div>
        </div>

        {error && <p className="status-message">{error}</p>}

        <div className="user-profile-actions">
          <Button
            type="button"
            variant="pill"
            onClick={() => onModeChange("tnp_standard")}
            disabled={saving || loading}
          >
            {siteTexts.profile.user.visualIdentity.restoreDefault}
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={onSave}
            disabled={saving || loading}
          >
            {saving
              ? siteTexts.profile.user.visualIdentity.saving
              : siteTexts.profile.user.visualIdentity.save}
          </Button>
        </div>
      </section>
    </ProfileAccordionSection>
  );
}
